import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { FormattedMessage } from 'react-intl'
import { Avatar, Text, ExternalLinkButton } from '../..'
import { Player } from '../Player'
import { SponsorBranding } from '../SponsorBranding'
import Phone from '../Phone'
import Email from '../Email'
import NMLS from '../NMLS'
import CustomerTitle from '../CustomerTitle'
import { safeExternal } from '../../../helpers/links'
import mockAgent from './mockAgent'
import QuestionSingle from './QuestionSingleContainer'

import styles from './Questions.module.scss'

const propTypes = {
  testDrive: PropTypes.bool,
  questions: PropTypes.array,
  questionTopic: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  questionTopicKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  customerProfile: PropTypes.object,
  sponsor: PropTypes.object,
  customerFirstName: PropTypes.string,
  sponsorshipFullName: PropTypes.string,
  sponsorshipFirstName: PropTypes.string,
  renderHeader: PropTypes.func,
  location: PropTypes.string,
  isBuyer: PropTypes.bool,
  disableCospo: PropTypes.bool,
  renderSponsorBranding: PropTypes.bool,
  sponsorVideoId: PropTypes.string,
  sponsorVideoType: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
}

class Questions extends PureComponent {
  static propTypes = propTypes

  isCoSpo() {
    const { customerProfile } = this.props
    const agent = this.getRenderedAgent()

    return agent !== customerProfile
  }

  getRenderedAgent() {
    const { customerProfile, isBuyer, sponsor, testDrive } = this.props

    if (sponsor && sponsor.id) {
      return sponsor
    }

    if (isBuyer && testDrive && customerProfile.type === 'real-estate-agents') {
      return mockAgent
    }

    return customerProfile
  }

  renderCoSpoMessage() {
    const {
      customerFirstName,
      sponsorshipFirstName,
      sponsorshipFullName,
      sponsorVideoId,
      sponsorVideoType,
      renderSponsorBranding,
    } = this.props

    return (
      <div className={styles.cospo}>
        {renderSponsorBranding && (
          <SponsorBranding className={styles.sponsorBranding} />
        )}
        {!renderSponsorBranding && (
          <Text
            variant='neutralCool'
            variantCount='700'
            size='s'
            className={styles.cospoMessage}
          >
            <FormattedMessage
              id='Questions.cospoMessage'
              defaultMessage="{customerName} speaks highly of {cospoName}, a lender that is happy to help answer questions for {customerName}'s clients and show how scenarios can save money."
              values={{
                customerName: customerFirstName,
                cospoName: (
                  <Text element='span' variant='neutralCool' variantCount='900'>
                    {sponsorshipFullName}
                  </Text>
                ),
              }}
            />
          </Text>
        )}
        {sponsorVideoId && sponsorVideoType && (
          <Player
            className={styles.sponsorVideo}
            videoId={sponsorVideoId}
            videoType={sponsorVideoType}
          />
        )}
        <h3>
          <FormattedMessage
            id='RefiDetailsModal.questions.ask'
            defaultMessage='Any questions for {name}?'
            values={{ name: sponsorshipFirstName }}
          />
        </h3>
      </div>
    )
  }

  renderAgent() {
    const agent = this.getRenderedAgent()
    const phone = get(agent, 'mobilePhone', agent.workPhone)

    return (
      <div className={styles.agentContainer} data-qa='contactInfo'>
        <Avatar className={styles.avatar} src={agent.photoUri || agent.photo} />
        <div className={styles.agentContact}>
          <NMLS
            size='smallest'
            variant='neutralCool'
            variantCount='700'
            value={agent.nmls}
          />

          <div className={styles.agentHeader}>
            <CustomerTitle size='s' value={agent.title} />

            <Text weight='bold' variant='neutralCool' variantCount='900'>
              {agent.firstName} {agent.lastName}
            </Text>

            <Email className={styles.email} value={agent.email} />
          </div>

          <div className={styles.agentSocial}>
            {phone && (
              <div>
                <Text
                  size='s'
                  weight='semibold'
                  variant='neutralCool'
                  variantCount='800'
                >
                  Phone
                </Text>
                <Phone value={phone} />
              </div>
            )}
            {agent.websiteUri && (
              <div>
                <Text
                  size='s'
                  weight='semibold'
                  variant='neutralCool'
                  variantCount='800'
                >
                  Website
                </Text>
                <ExternalLinkButton
                  className={styles.agentWebsite}
                  minimal
                  href={safeExternal(agent.websiteUri)}
                >
                  {agent.websiteUri}
                </ExternalLinkButton>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  render() {
    const {
      children,
      disableCospo,
      questions,
      questionTopic,
      questionTopicKey,
      renderHeader,
    } = this.props

    const renderSponsorContent = this.isCoSpo() && !disableCospo
    const agent = this.getRenderedAgent()

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          {typeof renderHeader === 'function' ? (
            <div className={styles.headline}>{renderHeader()}</div>
          ) : (
            <h2 className={styles.headline}>
              <FormattedMessage
                id='Questions.headline'
                defaultMessage='Want to learn more?'
              />
            </h2>
          )}
          <Text
            element='span'
            align='center'
            variant='neutralCool'
            variantCount='700'
          >
            Ask your expert
          </Text>
          {agent.corporationName && (
            <Text element='span' variant='neutralCool' variantCount='700'>
              {' '}
              from <strong>{agent.corporationName}</strong>
            </Text>
          )}
          <Text
            element='span'
            align='center'
            variant='neutralCool'
            variantCount='700'
          >
            {' '}
            to help answer what's on your mind.{' '}
          </Text>
        </div>
        <hr className={styles.divider} />
        <hr className={styles.divider} />

        {renderSponsorContent && this.renderCoSpoMessage()}
        {children}
        <div data-qa='dm-message-container' className={styles.messageContainer}>
          <div>
            <div className={styles.questions}>
              {questions.map(({ question, placeholder }, i) => (
                <QuestionSingle
                  key={i}
                  name={`input_${i}`}
                  question={question}
                  placeholder={placeholder}
                  questionTopic={
                    Array.isArray(questionTopic) && questionTopic.length > i
                      ? questionTopic[i]
                      : questionTopic
                  }
                  questionTopicKey={
                    Array.isArray(questionTopicKey) &&
                    questionTopicKey.length > i
                      ? questionTopicKey[i]
                      : questionTopicKey
                  }
                />
              ))}
            </div>
          </div>
          <div className={styles.contactInfo}>{this.renderAgent()}</div>
        </div>
      </div>
    )
  }
}

export default Questions
